import { RedoClient } from "@redotech/redo-api-client";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  CHECKOUT_OPTIMIZATION_TREATMENT_ID_KEY,
  CHECKOUT_OPTIMIZATION_TREATMENT_ID_QUERY_PARAM,
  COVERAGE_TREATMENT_ID_QUERY_PARAM,
  RESCUE_PANEL_TREATMENT_ID_LOCAL_STORAGE_KEY,
} from "@redotech/redo-web/utils/shared-conf";
import { lazy } from "@redotech/util/cache";
import {
  getAutocheck,
  ShopifyExtensionClient,
} from "./client/shopify-extension-client";
import { getRedoStorefrontConfig } from "./config";
import { PreviewModeState } from "./preview-mode/preview-state";
import { getShopperLanguage, getStorefrontLocale } from "./storefront-locale";
import { getLocalStorageIgnoreExpiry } from "./utils";

let widgetAutoCheckPromise: Promise<WidgetAutocheck> | null = null;
let widgetAutoCheckCustomerId: string | null = null;
let widgetAutoCheckLocalization = "";

function getActiveLocalization() {
  const locale = getStorefrontLocale();
  return {
    language: getShopperLanguage(),
    country: globalThis.Shopify?.country ?? locale.country,
  };
}

export const AUTOCHECK_CACHE_KEY = "redo_autocheck";

interface CachedAutocheck {
  autocheck: WidgetAutocheck;
  currency?: string;
  customerId: string | null;
  language?: string;
  country?: string;
}

function getStorefrontCustomerId(): string | null {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const customerId = (globalThis as any).ShopifyAnalytics?.meta?.page
    ?.customerId;
  return customerId ? String(customerId) : null;
}

function getCurrentForeignCurrency(): string | undefined {
  if (!!globalThis.Tapcart && !globalThis.Shopify) {
    return globalThis.Tapcart.variables.cart.currency;
  }
  return globalThis?.Shopify?.currency?.active;
}

function resetAutocheckPromiseIfIdentityChanged(
  currentCustomerId: string | null,
) {
  if (
    widgetAutoCheckPromise &&
    (widgetAutoCheckCustomerId !== currentCustomerId ||
      widgetAutoCheckLocalization !== JSON.stringify(getActiveLocalization()))
  ) {
    widgetAutoCheckPromise = null;
  }
}

function getAutocheckPromise(bypassCache = false): Promise<WidgetAutocheck> {
  let foreignCurrency: string | undefined;
  let currencyExchangeRate: string | undefined;

  if (!!globalThis.Tapcart && !globalThis.Shopify) {
    foreignCurrency = globalThis.Tapcart.variables.cart.currency;
    currencyExchangeRate = undefined;
  } else {
    foreignCurrency = globalThis?.Shopify?.currency?.active;
    currencyExchangeRate = globalThis?.Shopify?.currency?.rate;
  }

  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);
  const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
  const urlCheckoutOptimizationTreatmentId = urlParams.get(
    CHECKOUT_OPTIMIZATION_TREATMENT_ID_QUERY_PARAM,
  );

  const onPreview = PreviewModeState.getActiveAndSet();
  const localStorageTreatmentId =
    getLocalStorageIgnoreExpiry("redo_treatment_id");

  const desiredTreatment = onPreview
    ? urlID || localStorageTreatmentId || undefined
    : undefined;

  const localStorageCheckoutOptimizationTreatmentId: string | null =
    getLocalStorageWithExpiry(CHECKOUT_OPTIMIZATION_TREATMENT_ID_KEY);
  const localStorageRescuePanelTreatmentId: string | null =
    getLocalStorageWithExpiry(RESCUE_PANEL_TREATMENT_ID_LOCAL_STORAGE_KEY);

  // In preview mode, a forced treatment id from the URL overrides the visitor's
  // sticky (localStorage) assignment so merchants can preview a specific
  // variant. Outside preview we keep the sticky assignment for bucketing.
  const desiredCheckoutOptimizationTreatmentId =
    onPreview && urlCheckoutOptimizationTreatmentId
      ? urlCheckoutOptimizationTreatmentId
      : (localStorageCheckoutOptimizationTreatmentId ?? undefined);

  const redoClient = new RedoClient(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    globalThis.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
  );
  const client = new ShopifyExtensionClient(
    redoClient,
    globalThis.redoWidgetId,
    getRedoStorefrontConfig().storeUrl,
  );

  const customerId = getStorefrontCustomerId();

  const localization = getActiveLocalization();
  const promise = getAutocheck(client, {
    bypassCache,
    customerId,
    ...localization,
    foreignCurrency,
    currencyExchangeRate,
    desiredTreatment,
    desiredCheckoutOptimizationTreatmentId,
    desiredRescuePanelTreatmentId:
      localStorageRescuePanelTreatmentId ?? undefined,
  });
  if (!bypassCache) {
    widgetAutoCheckPromise = promise;
    widgetAutoCheckCustomerId = customerId;
    widgetAutoCheckLocalization = JSON.stringify(localization);
  }
  return promise;
}

export const getCartResponseCaptureEnabled = lazy(async () => {
  try {
    const candidate = await getAutocheckCache();
    return (
      candidate.cartResponseCaptureEnabled === true &&
      (await getAutocheckPromise(true)).cartResponseCaptureEnabled === true
    );
  } catch {
    return false;
  }
});

async function refreshAutocheckCache() {
  const localization = getActiveLocalization();
  const customerId = getStorefrontCustomerId();
  resetAutocheckPromiseIfIdentityChanged(customerId);
  const auto = widgetAutoCheckPromise
    ? await widgetAutoCheckPromise
    : await getAutocheckPromise();
  const cached: CachedAutocheck = {
    autocheck: auto,
    currency: getCurrentForeignCurrency(),
    customerId,
    ...localization,
  };
  setLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY, cached, 0.5);
}

export async function getAutocheckCache(): Promise<WidgetAutocheck> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const stored = getLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY) as
    | CachedAutocheck
    | WidgetAutocheck
    | null;
  // Cached entries may pre-date the currency field: treat anything without
  // an `autocheck` key as a legacy bare-autocheck payload.
  const storedAutocheck: WidgetAutocheck | undefined =
    stored && "autocheck" in stored
      ? stored.autocheck
      : ((stored as WidgetAutocheck | null) ?? undefined);
  const storedCurrency: string | undefined =
    stored && "autocheck" in stored ? stored.currency : undefined;
  const storedCustomerId: string | null | undefined =
    stored && "customerId" in stored ? stored.customerId : undefined;
  const { language, country } = getActiveLocalization();
  const localizationMatches =
    stored &&
    "autocheck" in stored &&
    stored.language === language &&
    stored.country === country;
  const currentCurrency = getCurrentForeignCurrency();
  const currentCustomerId = getStorefrontCustomerId();
  // Skip the cache if it was populated under a different presentment
  // currency — its `pricePerOrder` is converted to that currency and would
  // poison coverage pricing until the next background refresh (COVG-967).
  const cacheCurrencyMatches =
    !currentCurrency || !storedCurrency || storedCurrency === currentCurrency;
  // The local cache blob is not keyed on the forced treatment, so serving it
  // in preview mode would paint a stale variant. Skip it and fetch fresh.
  const onPreview = PreviewModeState.getActiveAndSet();
  if (
    storedAutocheck &&
    cacheCurrencyMatches &&
    localizationMatches &&
    storedCustomerId === currentCustomerId &&
    !onPreview
  ) {
    void refreshAutocheckCache();
    return storedAutocheck;
  }

  resetAutocheckPromiseIfIdentityChanged(currentCustomerId);
  if (widgetAutoCheckPromise === null) {
    widgetAutoCheckPromise = getAutocheckPromise();
  }

  try {
    const autocheck = await widgetAutoCheckPromise;
    const cached: CachedAutocheck = {
      autocheck,
      currency: getCurrentForeignCurrency(),
      customerId: currentCustomerId,
      language,
      country,
    };
    setLocalStorageWithExpiry(AUTOCHECK_CACHE_KEY, cached, 0.1);

    return autocheck;
  } catch (error) {
    widgetAutoCheckPromise = null;
    widgetAutoCheckCustomerId = null;
    throw error;
  }
}
